import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Room, RoomEvent, ConnectionState, RpcInvocationData } from 'livekit-client';
import { getLoggedInUser, getSelectedAgent } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { AnimatePresence, motion } from 'framer-motion';

import {
	RoomAudioRenderer,
	RoomContext,
	VoiceAssistantControlBar,
	useVoiceAssistant,
} from '@livekit/components-react';
import '@livekit/components-styles';
import TranscriptionView from './TransctionView';
import { safeJSONParse } from '@/utils/helper';

type ConnectionDetails = {
	participantToken: string;
};

const SERVER_URL = import.meta.env.VITE_LIVEKIT_SERVER_URL;
const MAX_CALL_DURATION = 15 * 60;

const CallComponent: React.FC<{ handleStopAudio: () => void }> = ({ handleStopAudio }) => {
	const loggedInUser = useAppSelector(getLoggedInUser);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const [room] = useState(() => new Room());
	const [isConnecting, setIsConnecting] = useState(false);
	const [isConnected, setIsConnected] = useState(false);
	const [workflowText, setWorkflowText] = useState('');
	const [showBanner, setShowBanner] = useState(false);
	const [hasPermission, setHasPermission] = useState<boolean | null>(null);
	const connectRequestSent = useRef(false);
	const apiRequestTimeout = useRef<NodeJS.Timeout | null>(null);
	const disconnectTimeout = useRef<NodeJS.Timeout | null>(null);
	const callTimerInterval = useRef<NodeJS.Timeout | null>(null);
	const lastActivityTime = useRef(Date.now());

	const formatTime = (seconds: number) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	useEffect(() => {
		checkMicrophonePermission();
	}, []);

	const checkMicrophonePermission = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach((track) => track.stop());
			setHasPermission(true);
		} catch (error) {
			console.error('Microphone permission denied:', error);
			setHasPermission(false);
		}
	};

	const requestMicrophonePermission = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach((track) => track.stop());
			setHasPermission(true);
		} catch (error) {
			console.error('Microphone permission denied:', error);
			setHasPermission(false);
		}
	};

	useEffect(() => {
		if (!SERVER_URL) {
			console.error('Missing required environment variables: VITE_LIVEKIT_SERVER_URL');
		}
	}, []);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.hidden && isConnected) {
				disconnectTimeout.current = setTimeout(() => {
					disconnectCall();
				}, 30000);
			} else if (!document.hidden && disconnectTimeout.current) {
				clearTimeout(disconnectTimeout.current);
				disconnectTimeout.current = null;
			}
		};

		const handleActivity = () => {
			lastActivityTime.current = Date.now();
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		document.addEventListener('mousemove', handleActivity);
		document.addEventListener('keypress', handleActivity);

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			document.removeEventListener('mousemove', handleActivity);
			document.removeEventListener('keypress', handleActivity);
			if (disconnectTimeout.current) {
				clearTimeout(disconnectTimeout.current);
			}
		};
	}, [isConnected]);

	useEffect(() => {
		const checkInactivity = setInterval(() => {
			if (isConnected) {
				const inactiveTime = Date.now() - lastActivityTime.current;
				if (inactiveTime > 300000) {
					disconnectCall();
				}
			}
		}, 60000);

		return () => clearInterval(checkInactivity);
	}, [isConnected]);

	const onConnectButtonClicked = useCallback(async () => {
		if (hasPermission === false) {
			await requestMicrophonePermission();
			if (!hasPermission) {
				return;
			}
		}

		if (isConnecting || connectRequestSent.current) {
			return;
		}

		if (!SERVER_URL) {
			console.error('Missing required LiveKit configuration');
			return;
		}

		setIsConnecting(true);
		connectRequestSent.current = true;

		if (apiRequestTimeout.current) {
			clearTimeout(apiRequestTimeout.current);
		}

		try {
			const response = await fetch(
				`${import.meta.env.VITE_APP_API_BASE_URL}/api/playground/generate-token`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						username: selectedAgent?.number,
						is_reseller: !!loggedInUser?.resellerId,
						testing_number: selectedAgent?.testing_number,
					}),
					cache: 'no-store',
				},
			);

			if (!response.ok) {
				throw new Error(`Failed to fetch token: ${response.statusText}`);
			}

			const connectionDetailsData: ConnectionDetails = await response.json();

			console.log('Connecting to LiveKit with:', {
				serverUrl: SERVER_URL,
				token: connectionDetailsData.participantToken ? 'Token received' : 'No token',
			});

			await room.connect(SERVER_URL, connectionDetailsData.participantToken);
			await room.localParticipant.setMicrophoneEnabled(true);

			lastActivityTime.current = Date.now();
			console.log('Successfully connected and microphone enabled');

			// execution workflow creation
			try {
				room.registerRpcMethod('workflowExecuted', async (data: RpcInvocationData) => {
					const message = safeJSONParse(data.payload, {});
					if (message && message['data'] !== undefined) {
						showWorkflowNotification(message['data']);
					} else {
						console.log('Payload type must be object');
					}

					return 'workflow notification triggered';
				});
			} catch (error) {
				console.error('RPC Could not be registered - Code: ', error);
			}
		} catch (error) {
			console.error('Connection error:', error);
			connectRequestSent.current = false;
		}
	}, [room, selectedAgent?.number, selectedAgent?.testing_number, isConnecting, hasPermission]);

	const disconnectCall = useCallback(() => {
		room.disconnect();
		connectRequestSent.current = false;

		if (apiRequestTimeout.current) {
			clearTimeout(apiRequestTimeout.current);
		}
		if (disconnectTimeout.current) {
			clearTimeout(disconnectTimeout.current);
		}
		if (callTimerInterval.current) {
			clearInterval(callTimerInterval.current);
		}

		setIsConnected(false);

		if (handleStopAudio) {
			handleStopAudio();
		}
	}, [room, handleStopAudio]);

	useEffect(() => {
		const handleConnected = () => {
			console.log('Room connected event');
			connectRequestSent.current = false;
		};

		const handleDisconnected = () => {
			if (isConnecting) {
				// Agent dropped the call due to some reason, which means the client is not allowed to conenct.
				// So we need to set the connecting state to false. and give a notification to the user.
				setIsConnecting(false);
				return;
			}

			console.log('Room disconnected event');
			setIsConnected(false);
			connectRequestSent.current = false;
			if (apiRequestTimeout.current) {
				clearTimeout(apiRequestTimeout.current);
			}
		};

		const handleConnectionStateChanged = (state: ConnectionState) => {
			console.log('Connection state changed:', state);
		};

		room.on(RoomEvent.Connected, handleConnected);
		room.on(RoomEvent.Disconnected, handleDisconnected);
		room.on(RoomEvent.MediaDevicesError, onDeviceFailure);
		room.on(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);
		room.on(RoomEvent.ConnectionQualityChanged, (quality) => {
			if (quality === 'poor') {
				console.warn('Poor connection quality detected');
			}
		});

		return () => {
			room.off(RoomEvent.Connected, handleConnected);
			room.off(RoomEvent.Disconnected, handleDisconnected);
			room.off(RoomEvent.MediaDevicesError, onDeviceFailure);
			room.off(RoomEvent.ConnectionStateChanged, handleConnectionStateChanged);

			if (room.state === ConnectionState.Connected) {
				room.disconnect();
			}
		};
	}, [room]);

	function showWorkflowNotification(payload: string) {
		if (!payload) {
			console.log('payload empty');
			return;
		}
		setWorkflowText(payload);
		setShowBanner(true);

		setTimeout(() => {
			setShowBanner(() => {
				setWorkflowText('');
				return false;
			});
		}, 6000);
	}

	return (
		<RoomContext.Provider value={room}>
			<div className="flex flex-col h-full w-full bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-2xl">
				<SimpleVoiceAssistant
					onConnectButtonClicked={onConnectButtonClicked}
					isConnecting={isConnecting}
					onDisconnect={disconnectCall}
					hasPermission={hasPermission}
					requestPermission={requestMicrophonePermission}
					setIsConnecting={setIsConnecting}
					formatTime={formatTime}
					workflowText={workflowText}
					showBanner={showBanner}
					setShowBanner={setShowBanner}
				/>
			</div>
		</RoomContext.Provider>
	);
};

export default CallComponent;

function SimpleVoiceAssistant(props: {
	onConnectButtonClicked: () => void;
	isConnecting: boolean;
	onDisconnect: () => void;
	hasPermission: boolean | null;
	requestPermission: () => void;
	formatTime: (seconds: number) => string;
	setIsConnecting: (isConnecting: boolean) => void;
	workflowText: string;
	showBanner: boolean;
	setShowBanner: (show: boolean) => void;
}) {
	const { state: agentState } = useVoiceAssistant();
	const [callDuration, setCallDuration] = useState(0);
	const callTimerInterval = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (agentState === 'listening' || agentState === 'speaking' || agentState === 'thinking') {
			console.log('Agent is active, starting timer');

			props.setIsConnecting(false);

			if (!callTimerInterval.current) {
				setCallDuration(0);

				callTimerInterval.current = setInterval(() => {
					setCallDuration((prev) => {
						const newDuration = prev + 1;
						console.log('Timer tick:', newDuration);

						if (newDuration >= MAX_CALL_DURATION) {
							console.log('Max duration reached, disconnecting...');
							props.onDisconnect();
						}
						return newDuration;
					});
				}, 1000);
			}
		} else {
			console.log('Agent not active, stopping timer');
			if (callTimerInterval.current) {
				clearInterval(callTimerInterval.current);
				callTimerInterval.current = null;
			}

			if (agentState === 'disconnected') {
				setCallDuration(0);
			}
		}

		return () => {
			if (agentState === 'disconnected' && callTimerInterval.current) {
				clearInterval(callTimerInterval.current);
				callTimerInterval.current = null;
			}
		};
	}, [agentState]);

	return (
		<div className="flex flex-col h-full w-full">
			<AnimatePresence mode="wait">
				{agentState === 'disconnected' ||
				agentState === 'connecting' ||
				agentState === 'initializing' ? (
					<motion.div
						key="disconnected"
						initial={{ opacity: 0, scale: 0.95 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						transition={{ duration: 0.3 }}
						className="flex items-center justify-center flex-grow"
					>
						{props.hasPermission === false ? (
							<motion.button
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								className="px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white rounded-full font-semibold shadow-xl transition-all duration-200"
								onClick={props.requestPermission}
							>
								<div className="flex items-center">
									<svg
										className="w-6 h-6 mr-3"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
										/>
									</svg>
									Grant Microphone Permission
								</div>
							</motion.button>
						) : (
							<motion.button
								whileHover={{ scale: 1.05 }}
								whileTap={{ scale: 0.95 }}
								className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full font-semibold shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
								onClick={props.onConnectButtonClicked}
								disabled={props.isConnecting || props.hasPermission !== true}
							>
								{props.isConnecting ? (
									<div className="flex items-center">
										<svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											/>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											/>
										</svg>
										Connecting...
									</div>
								) : (
									<div className="flex items-center">
										<svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 24 24">
											<path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
											<path d="M17.3 11c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
										</svg>
										Start Conversation
									</div>
								)}
							</motion.button>
						)}
					</motion.div>
				) : (
					<motion.div
						key="connected"
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -20 }}
						transition={{ duration: 0.3 }}
						className="flex flex-col h-full w-full gap-4"
					>
						{/* Timer Display */}
						<div className="flex justify-center">
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								className="bg-white rounded-2xl px-6 py-3 shadow-xl flex items-center gap-4"
							>
								<svg
									className="w-6 h-6 text-indigo-600"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<div className="flex items-center gap-3">
									<span
										className={`text-2xl font-bold ${callDuration >= 870 ? 'text-red-600' : 'text-gray-800'}`}
									>
										{props.formatTime(callDuration)}
									</span>
									<span className="text-gray-300">|</span>
									<span className="text-lg text-gray-500">
										{props.formatTime(MAX_CALL_DURATION)}
									</span>
								</div>
								{/* 30 secs before ends */}
								{callDuration >= 870 && (
									<motion.span
										initial={{ opacity: 0, x: -10 }}
										animate={{ opacity: 1, x: 0 }}
										className="text-sm text-red-600 font-semibold ml-2"
									>
										Ending soon
									</motion.span>
								)}
							</motion.div>
						</div>

						<div className="w-full bg-white rounded-2xl shadow-xl p-6">
							<AgentVisualizer />
						</div>

						{/*Workflow Notification UI*/}
						<AnimatePresence>
							{/* workflow status Banner */}
							{props.showBanner && (
								<motion.div
									initial={{ opacity: 0, y: -10 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -10 }}
									className="w-full bg-blue-100 text-blue-900 px-4 py-3 rounded-xl shadow flex justify-between items-center"
								>
									{props.workflowText && props.workflowText?.toLowerCase().includes('error')
										? 'An ERROR occurred triggering this workflow...'
										: `${props.workflowText} workflow executed Successfully!`}
									<button onClick={() => props.setShowBanner(false)} className="text-xl font-bold">
										&times;
									</button>
								</motion.div>
							)}
						</AnimatePresence>

						<div className="flex-1 w-full bg-white rounded-2xl shadow-xl p-4 overflow-hidden">
							<TranscriptionView />
						</div>
						<div className="w-full">
							<ControlBar
								onConnectButtonClicked={props.onConnectButtonClicked}
								onDisconnect={props.onDisconnect}
								agentState={agentState}
							/>
						</div>
						<RoomAudioRenderer />
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

function AgentVisualizer() {
	const { state: agentState } = useVoiceAssistant();

	return (
		<div className="relative w-full">
			<div className="w-full h-56 rounded-2xl overflow-hidden bg-slate-50 shadow-inner flex items-center justify-center relative">
				<AnimatePresence mode="wait">
					{/* Listening State */}
					{agentState === 'listening' && (
						<motion.div
							key="listening"
							className="flex items-center justify-center"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							<motion.div
								className="w-20 h-20 rounded-full bg-purple-500"
								animate={{
									scale: [1, 1.2, 1],
									opacity: [0.8, 0.5, 0.8],
								}}
								transition={{
									duration: 1.5,
									repeat: Infinity,
									ease: 'easeInOut',
								}}
							/>
							<div className="absolute w-12 h-12 rounded-full bg-purple-600" />
						</motion.div>
					)}

					{/* Speaking State */}
					{agentState === 'speaking' && (
						<motion.div
							key="speaking"
							className="flex items-center justify-center gap-2"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							{[...Array(6)].map((_, i) => (
								<motion.div
									key={i}
									className="w-2 bg-blue-500 rounded-full"
									animate={{
										height: [16, 40 + Math.sin(i) * 20, 16],
									}}
									transition={{
										duration: 0.8,
										repeat: Infinity,
										delay: i * 0.1,
										ease: 'easeInOut',
									}}
								/>
							))}
						</motion.div>
					)}

					{/* Thinking State */}
					{agentState === 'thinking' && (
						<motion.div
							key="thinking"
							className="flex items-center justify-center gap-3"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							{[...Array(3)].map((_, i) => (
								<motion.div
									key={i}
									className="w-4 h-4 bg-indigo-500 rounded-full"
									animate={{
										y: [0, -12, 0],
									}}
									transition={{
										duration: 0.6,
										repeat: Infinity,
										delay: i * 0.2,
										ease: 'easeInOut',
									}}
								/>
							))}
						</motion.div>
					)}

					{/* Default State */}
					{(agentState === 'connecting' || agentState === 'disconnected') && (
						<motion.div
							key="idle"
							className="flex items-center justify-center"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
						>
							<motion.div
								className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-400 to-blue-500"
								animate={{
									scale: [1, 1.05, 1],
								}}
								transition={{
									duration: 3,
									repeat: Infinity,
									ease: 'easeInOut',
								}}
							/>
						</motion.div>
					)}
				</AnimatePresence>

				{/* Status text overlay */}
				<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/20 to-transparent p-4">
					<div className="text-center">
						<div className="text-sm font-medium text-gray-700">
							{agentState === 'listening' && 'Listening to you...'}
							{agentState === 'speaking' && 'Speaking'}
							{agentState === 'thinking' && 'Processing...'}
							{agentState === 'connecting' && 'Connecting...'}
							{agentState === 'disconnected' && 'Disconnected'}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function ControlBar(props: {
	onConnectButtonClicked: () => void;
	onDisconnect?: () => void;
	agentState: string;
}) {
	return (
		<div className="relative h-20 flex items-center justify-center px-4">
			<AnimatePresence>
				{props.agentState === 'disconnected' && (
					<motion.button
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.3 }}
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
						className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-full font-semibold shadow-xl transition-all duration-200"
						onClick={() => props.onConnectButtonClicked()}
					>
						<div className="flex items-center">
							<MicrophoneIcon className="mr-3" />
							Start Conversation
						</div>
					</motion.button>
				)}
			</AnimatePresence>
			<AnimatePresence>
				{props.agentState !== 'disconnected' && props.agentState !== 'connecting' && (
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -10 }}
						transition={{ duration: 0.3 }}
						className="flex gap-6 items-center"
					>
						<VoiceAssistantControlBar
							controls={{ leave: false }}
							className="rounded-full bg-white shadow-lg px-6 py-3"
						/>
						<motion.button
							className="px-8 py-4 bg-red-600 hover:bg-red-700 text-white rounded-full font-semibold shadow-xl transition-all duration-200 flex items-center gap-3"
							onClick={props.onDisconnect}
							whileHover={{ scale: 1.05 }}
							whileTap={{ scale: 0.95 }}
						>
							<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
							End Call
						</motion.button>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
}

function MicrophoneIcon({ className = '' }) {
	return (
		<svg
			className={className}
			width="20"
			height="20"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M12 2C10.9 2 10 2.9 10 4V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V4C14 2.9 13.1 2 12 2Z"
				fill="currentColor"
			/>
			<path
				d="M19 12C19 16.13 15.74 19.44 11.67 19.94V22H16V24H8V22H12.33V19.94C8.26 19.44 5 16.13 5 12H7C7 15.24 9.56 18 13 18C16.44 18 19 15.44 19 12H21C21 12.47 20.9 12.92 20.82 13.37C20.21 16.68 17.33 19.22 13.81 19.92C10.29 20.61 6.68 19.05 4.93 16.07C3.18 13.09 3.94 9.34 6.65 7.04C9.35 4.74 13.28 4.39 16.4 6.15C17.77 6.89 18.88 8.05 19.59 9.44C19.85 10.02 20 10.51 20 11V12H19Z"
				fill="currentColor"
			/>
		</svg>
	);
}

function onDeviceFailure(error: Error) {
	console.error(error);
}
